import React, { useMemo, useRef, useEffect } from "react";
import { Box } from "@mui/material";
import { format } from "date-fns";
import {
  DataGrid,
  GridFooterContainer,
  useGridApiContext,
  useGridSelector,
  gridVisibleColumnDefinitionsSelector,
} from "@mui/x-data-grid";
import { CustomNoRowsOverlay } from "../../../../components/common/OverlayGrid";
import { commonDataGridSx } from "../../../../components/common/commonDataGridStyles";
import { useTheme } from "../../../../ThemeContext";

export const formatMonthLabel = (monthStr) => {
  if (!monthStr) return "—";
  try {
    if (String(monthStr).includes("-")) {
      const parts = String(monthStr).split("-");
      if (parts.length === 2) {
        const d = new Date(Number(parts[0]), Number(parts[1]) - 1, 1);
        return format(d, "MMM yyyy");
      }
    }
    return monthStr;
  } catch {
    return monthStr;
  }
};

const MonthlyStickyFooter = ({ monthlyCount, totalHours, summary, hasData }) => {
  const { isDarkMode } = useTheme();
  const apiRef = useGridApiContext();
  const visibleColumns = useGridSelector(apiRef, gridVisibleColumnDefinitionsSelector);
  const scrollRef = useRef(null);

  useEffect(() => {
    const rootEl = apiRef.current?.rootElementRef?.current;
    if (!rootEl) return;
    const virtualScroller = rootEl.querySelector(".MuiDataGrid-virtualScroller");
    if (!virtualScroller) return;

    const handleScroll = () => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = virtualScroller.scrollLeft;
      }
    };

    virtualScroller.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      virtualScroller.removeEventListener("scroll", handleScroll);
    };
  }, [apiRef]);

  if (!hasData || !visibleColumns || visibleColumns.length === 0) return null;

  return (
    <GridFooterContainer
      sx={{
        backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
        borderTop: isDarkMode ? "1px solid #333" : "2px solid #e0e0e0",
        minHeight: "48px",
        height: "48px",
        p: 0,
        m: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          display: "flex",
          width: "100%",
          overflowX: "hidden",
          alignItems: "center",
          height: "48px",
        }}
      >
        {visibleColumns.map((col) => {
          let content = null;
          let color = isDarkMode ? "#f1f5f9" : "#1e293b";
          let fontWeight = 700;

          if (col.field === "month") {
            content = `${monthlyCount} months Total`;
          } else if (col.field === "hours") {
            content = totalHours % 1 === 0 ? totalHours : totalHours.toFixed(2);
          } else if (col.field === "util") {
            content = summary?.utilisation != null ? `${summary.utilisation}%` : "—";
            color = Number(summary?.utilisation || 0) < 20 ? (isDarkMode ? "#f87171" : "#dc2626") : (isDarkMode ? "#f1f5f9" : "#374151");
          } else if (col.field === "loadedCost") {
            content = Number(summary?.loaded_cost || 0).toLocaleString("en-IN");
          } else if (col.field === "realisedEarning") {
            content = Number(summary?.realised_earning || 0).toLocaleString("en-IN");
            color = isDarkMode ? "#4ade80" : "#16a34a";
          } else if (col.field === "ratio") {
            content = summary?.ratio != null ? `${summary.ratio}x` : "—";
            color = Number(summary?.ratio || 0) >= 1 ? (isDarkMode ? "#4ade80" : "#16a34a") : (isDarkMode ? "#fbbf24" : "#d97706");
          } else if (col.field === "source" || col.field === "note") {
            content = "";
            color = isDarkMode ? "#94a3b8" : "#6b7280";
            fontWeight = 400;
          }

          return (
            <Box
              key={col.field}
              sx={{
                width: col.computedWidth,
                minWidth: col.computedWidth,
                maxWidth: col.computedWidth,
                px: "10px",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                fontSize: "13px",
                fontWeight,
                color,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {content}
            </Box>
          );
        })}
      </Box>
    </GridFooterContainer>
  );
};

const MonthlyHistory = ({
  selectedUserId,
  monthlyHistory = [],
  summary = {},
  monthlyRangeText = "All time",
  isLoading = false,
  isDarkMode = false,
}) => {
  const monthlyColumns = useMemo(
    () => [
      {
        field: "month",
        headerName: "Month",
        flex: 1.1,
        minWidth: 120,
        renderCell: (params) => (
          <span
            style={{
              fontWeight: params.row.isTotal ? 700 : 500,
              color: params.row.isTotal ? "#1e293b" : "#374151",
              fontSize: "13px",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "hours",
        headerName: "Hours",
        flex: 0.8,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span
            style={{
              fontWeight: params.row.isTotal ? 700 : 400,
              color: "#374151",
              fontSize: "13px",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "util",
        headerName: "Util.",
        flex: 0.8,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span
            style={{
              color: params.row.utilType === "danger" ? "#dc2626" : "#374151",
              fontWeight: params.row.isTotal
                ? 700
                : params.row.utilType === "danger"
                ? 500
                : 400,
              fontSize: "13px",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "loadedCost",
        headerName: "Loaded cost",
        flex: 1.1,
        minWidth: 120,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => (
          <span
            style={{
              fontWeight: params.row.isTotal ? 700 : 400,
              color: "#374151",
              fontSize: "13px",
            }}
          >
            {params.value}
          </span>
        ),
      },
      {
        field: "realisedEarning",
        headerName: "Realised earning",
        flex: 1.2,
        minWidth: 130,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
          const isSuccess =
            params.value !== "0" &&
            Number(String(params.value).replace(/,/g, "")) > 0;
          const isZero = params.value === "0";
          return (
            <span
              style={{
                color: isZero ? "#9ca3af" : isSuccess ? "#16a34a" : "#374151",
                fontWeight: params.row.isTotal ? 700 : isSuccess ? 500 : 400,
                fontSize: "13px",
              }}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        field: "ratio",
        headerName: "Ratio",
        flex: 0.8,
        minWidth: 90,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
          const color =
            params.row.ratioType === "success"
              ? "#16a34a"
              : params.row.ratioType === "warning"
              ? "#d97706"
              : "#9ca3af";
          return (
            <span
              style={{
                fontWeight: 700,
                color,
                fontSize: "13px",
              }}
            >
              {params.value}
            </span>
          );
        },
      },
      {
        field: "source",
        headerName: "Source",
        flex: 1.5,
        minWidth: 160,
        renderCell: (params) => {
          if (!params.value || params.value === "—") {
            return <span style={{ color: "#9ca3af" }}>—</span>;
          }
          if (params.value === "Allocated") {
            return (
              <span className="badge-status-allocated">
                Allocated
              </span>
            );
          }
          return (
            <span style={{ color: "#6b7280", fontSize: "13px" }}>
              {params.value}
            </span>
          );
        },
      },
    ],
    []
  );

  const totalMonthlyHours = useMemo(() => {
    return monthlyHistory.reduce((acc, curr) => acc + Number(curr.hours || 0), 0);
  }, [monthlyHistory]);

  const monthlyRows = useMemo(() => {
    if (!selectedUserId || !Array.isArray(monthlyHistory) || monthlyHistory.length === 0)
      return [];
    return monthlyHistory.map((item, idx) => {
      const formattedMonth = formatMonthLabel(item.month);
      const ratioNum = Number(item.ratio || 0);
      const utilNum = Number(item.utilisation || 0);
      const hoursNum = Number(item.hours || 0);

      return {
        id: idx + 1,
        month: formattedMonth,
        hours: hoursNum % 1 === 0 ? String(hoursNum) : hoursNum.toFixed(2),
        util: item.utilisation != null ? `${item.utilisation}%` : "0%",
        utilType: utilNum < 20 ? "danger" : "normal",
        loadedCost: Number(item.loaded_cost || 0).toLocaleString("en-IN"),
        realisedEarning: Number(item.realised_earning || 0).toLocaleString("en-IN"),
        ratio: item.ratio != null ? `${item.ratio}x` : "—",
        ratioType:
          ratioNum >= 1 ? "success" : ratioNum > 0 ? "warning" : "default",
        received_amount: Number(item.received_amount || 0).toLocaleString("en-IN"),
        source: item.source || "Allocated",
        note: item.source || "Allocated",
        isTotal: false,
      };
    });
  }, [monthlyHistory, selectedUserId]);

  const isMonthlyScrollable = monthlyRows.length > 10;

  const ResourceNoRowsOverlay = () => {
    if (!selectedUserId) {
      return (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            fontSize: "16px",
            fontWeight: "bold",
            backgroundColor: isDarkMode ? "#272626" : "#f0f0f0",
            color: isDarkMode ? "white" : "#888",
          }}
        >
          Select resource and view resource earning
        </Box>
      );
    }
    return <CustomNoRowsOverlay />;
  };

  return (
    <div className={`card border rounded shadow-sm mb-4 ${isDarkMode ? "bg-dark border-secondary" : ""}`}>
      <div className={`card-header ${isDarkMode ? "bg-dark text-white border-secondary" : "bg-white"} py-3 border-bottom d-flex align-items-center justify-content-between`}>
        <div className="d-flex align-items-center gap-2">
          <span
            className="rounded-circle"
            style={{
              width: "12px",
              height: "12px",
              backgroundColor: "rgb(4, 161, 137)",
            }}
          ></span>
          <span className="fw-semibold" style={{ fontSize: "14px" }}>
            Monthly history — allocated only
          </span>
          <span className="text-muted small ms-2">{monthlyRangeText}</span>
        </div>
      </div>

      <Box
        sx={{
          width: "100%",
          backgroundColor: isDarkMode ? "#1e1e1e" : "#fff",
          height: isMonthlyScrollable ? 584 : "auto",
          overflow: "hidden",
        }}
      >
        <DataGrid
          rows={isLoading ? [] : monthlyRows}
          columns={monthlyColumns}
          loading={isLoading}
          autoHeight={!isMonthlyScrollable}
          hideFooterPagination
          disableRowSelectionOnClick
          getRowId={(row) => row.id}
          rowHeight={48}
          slots={{
            footer: MonthlyStickyFooter,
            noRowsOverlay: ResourceNoRowsOverlay,
          }}
          slotProps={{
            footer: {
              monthlyCount: monthlyHistory.length,
              totalHours: totalMonthlyHours,
              summary,
              hasData: Boolean(selectedUserId && !isLoading && monthlyHistory.length > 0),
            },
          }}
          sx={commonDataGridSx}
        />
      </Box>
    </div>
  );
};

export default MonthlyHistory;
